/*
 * File: SalesSupportInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 May 2021 1:43:47 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetUserQuery } from 'redux/services/useCookieMonster';
import { useGetEmployeesQuery } from 'redux/services/useLeftyTheSalesmanService';
import { BlockLoader } from '../BlockLoader';
const { Title } = Typography;

const styles: InlineStylesModel = {
  formItem: {
    marginBottom: 10,
    width: '100%'
  },
  formItemCol: {
    marginRight: '16px',
    maxHeight: '42px'
  },
  titleColor: {
    color: '#2F5596'
  }
};

export const SalesSupportInput = (): JSX.Element => {
  const {
    values: { salesSupportFullName, salesSupportPersonnelNumber },
    setFieldValue
  } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { user } = useSelector((state: ReduxState) => state.app);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);

  /* ****************** Hook API ****************** */
  const { data: userData } = useGetUserQuery(user?.sub || skipToken);

  const { data: salesEmployee, isLoading, isFetching } = useGetEmployeesQuery({ offset: 0, dataAreaId, overrideSkipTake: true }, { skip: !userData });
  const { data: personalSalesEmployee } = useGetEmployeesQuery(
    { offset: 0, dataAreaId, overrideSkipTake: true, employeeNameContains: inputValue, employeeErpId: userData?.user_metadata.employee_erp_id || undefined },
    { skip: !userData }
  );

  /* ****************** JSX / Variables ****************** */
  const salesSupportSearchOptions = useMemo(
    () => salesEmployee?.data?.map((employee) => ({ label: `${employee.name} - ${employee.personnelNumber}`, value: `${employee.name} - ${employee.personnelNumber}` })),
    [salesEmployee]
  );

  /* ****************** Functions ****************** */
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  //   _setInputValue(value);
  // }, 600);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setFieldValue('salesSupportFullName' as keyof SalesOrderPayloadModel, null);
    setFieldValue('salesSupportPersonnelNumber' as keyof SalesOrderPayloadModel, null);
  };

  // const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
  //   const target = event.currentTarget;

  //   if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && salesEmployee && salesEmployee?.totalCount - offset > 25) {
  //     _setOffset(offset + 25);
  //   }
  // };

  const handleSelect = (value: string): void => {
    setFieldValue('salesSupportFullName' as keyof SalesOrderPayloadModel, value.split(' - ')[0]);
    setFieldValue('salesSupportPersonnelNumber' as keyof SalesOrderPayloadModel, value.split(' - ')[1]);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ******************** Effects ******************** */
  useEffect(() => {
    if (personalSalesEmployee && personalSalesEmployee.data.length && !salesSupportFullName) {
      setFieldValue('salesSupportFullName' as keyof SalesOrderPayloadModel, personalSalesEmployee.data[0].name);
      setFieldValue('salesSupportPersonnelNumber' as keyof SalesOrderPayloadModel, personalSalesEmployee.data[0].personnelNumber);
    }
  }, [personalSalesEmployee, salesSupportFullName, setFieldValue]);

  /* ****************** Render ****************** */
  return (
    <>
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.SALES_SUPPORT}
      </Title>
      <Select
        value={salesSupportFullName ? `${salesSupportFullName} - ${salesSupportPersonnelNumber}` : undefined}
        allowClear
        dropdownRender={dropDownRender}
        // filterOption={false}
        filterOption={(input, option): boolean => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
        loading={isLoading || isFetching}
        onClear={handleClear}
        disabled={isPosted}
        // onPopupScroll={handleScroll}
        // onSearch={handleSearch}
        onSelect={handleSelect}
        options={salesSupportSearchOptions}
        showSearch
        style={styles.formItem}
      />
    </>
  );
};
